import { useState, useEffect } from 'react';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import {
    Box, Text, Button, Link
} from 'components/ui';
import store from 'Store';
import Actions from 'Actions';
import ErrorList from 'components/ErrorList';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import externalAuthService from 'services/api/authentication/externalAuth';
import UrlUtils from 'utils/Url';
import {
    colors, space, lineHeights, fontSizes, fontWeights
} from 'style/config';
import AuthGrantEN from './locales/AuthGrant_en_US';
import AuthGrantFR from './locales/AuthGrant_fr_CA';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(AuthGrantEN, AuthGrantFR);

/**
 * External Auth Authorization Grant Page
 * OAuth2.0 authorization page where user approves/denies third-party application access
 *
 * Flow:
 * 1. User arrives here after successful external auth login
 * 2. Retrieve session_id from localStorage or URL parameters
 * 3. Parse requested scopes from external auth params
 * 4. Display permissions list and third-party application information
 * 5. User reviews and clicks "Authorize" or "Deny"
 * 6. Submit decision via POST to /oauth2/grant API endpoint
 * 7. Redirect user to third-party app using redirectUrl from API response
 */
const AuthGrant = () => {
    const [errorMessages, setErrorMessages] = useState([]);
    const [sessionId, setSessionId] = useState(null);

    useEffect(() => {
        // Get session_id from URL params or localStorage
        store.dispatch(Actions.showInterstice(true));
        const getSessionId = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const sessionIdFromUrl = urlParams.get('session_id');
            const sessionIdFromStorage = Storage.local.getItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID);

            return sessionIdFromUrl || sessionIdFromStorage;
        };

        const currentSessionId = getSessionId();

        setSessionId(currentSessionId);

        if (!currentSessionId) {
            store.dispatch(Actions.showInterstice(false));
            setErrorMessages([
                {
                    errorMessage: getText('sessionIdNotFound')
                }
            ]);
            // TODO: Implement proper OAuth2 error redirect to third-party app

            return;
        }

        // Parse scopes from space-separated string
        store.dispatch(Actions.showInterstice(false));
    }, []);

    /**
     * Handle user's authorization decision (approve/deny)
     * Submits the decision to backend and redirects user to third-party app
     * @param {boolean} approved - Whether user approved or denied the authorization request
     */
    const handleGrant = async approved => {
        store.dispatch(Actions.showInterstice(true));
        setErrorMessages([]);

        try {
            const response = await externalAuthService.submitGrant(sessionId, approved);

            // Expected response contains redirectUrl to third-party app
            // Example: { redirectUrl: "https://chatgpt.com/connector_platform_oauth_redirect?code=..." }
            if (response.redirectUrl) {
                // Clean up external auth session data from localStorage
                Storage.local.removeItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID);
                Storage.local.removeItem(LOCAL_STORAGE.EXTERNAL_AUTH_PARAMS);
                store.dispatch(Actions.showInterstice(false));
                // Redirect user back to third-party application with OAuth2 authorization code
                UrlUtils.redirectTo(response.redirectUrl);
            } else {
                throw { errorMessage: getText('authorizationFailed') };
            }
        } catch (error) {
            // Handle 403 responses and error structure: {error: "invalid_session", errorDescription: "Session ID mismatch"}
            // Extract error message from various possible fields
            const errorMessage =
                error?.errorDescription || error?.error_description || error?.errorMessage || error?.message || getText('authorizationFailed');

            setErrorMessages([{ errorMessage }]);
            store.dispatch(Actions.showInterstice(false));
        }
    };

    const handleApprove = () => {
        handleGrant(true);
    };

    const handleCancel = () => {
        handleGrant(false);
    };

    /**
     * Render the requested permissions based on OAuth2 scopes
     * Maps scope strings to user-friendly permission descriptions
     */
    const renderPermissions = () => {
        return (
            <Box>
                <Box
                    display='flex'
                    alignItems='flex-start'
                    css={styles.permissionItem}
                >
                    <Text
                        fontSize={fontSizes.md}
                        css={styles.checkmark}
                    >
                        ✓
                    </Text>
                    <Text
                        fontSize={fontSizes.sm}
                        lineHeight={lineHeights.base}
                        color={colors.black}
                    >
                        {getText('permission1')}
                    </Text>
                </Box>
                <Box
                    display='flex'
                    alignItems='flex-start'
                    css={styles.permissionItem}
                >
                    <Text
                        fontSize={fontSizes.md}
                        css={styles.checkmark}
                    >
                        ✓
                    </Text>
                    <Text
                        fontSize={fontSizes.sm}
                        lineHeight={lineHeights.base}
                        color={colors.black}
                    >
                        {getText('permission2')}
                    </Text>
                </Box>
                <Box
                    display='flex'
                    alignItems='flex-start'
                >
                    <Text
                        fontSize={fontSizes.md}
                        css={styles.checkmark}
                    >
                        ✓
                    </Text>
                    <Text
                        fontSize={fontSizes.sm}
                        lineHeight={lineHeights.base}
                        color={colors.black}
                    >
                        {getText('permission3')}
                    </Text>
                </Box>
            </Box>
        );
    };

    return (
        <div>
            <Box
                minHeight='100vh'
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='flex-start'
                css={styles.pageContainer}
            >
                {/* Logo */}
                <Box css={styles.logoContainer}>
                    <img
                        src='/img/ufe/logo.svg'
                        alt='Sephora'
                        style={styles.logo}
                    />
                </Box>

                {/* Content Container with 400px max-width */}
                <Box
                    maxWidth='400px'
                    width='100%'
                >
                    {/* Error Messages */}
                    {errorMessages.length > 0 && (
                        <Box css={styles.errorContainer}>
                            <ErrorList errors={errorMessages} />
                        </Box>
                    )}

                    {/* Page Title */}
                    <Box css={{ marginBottom: `${space[5]}px` }}>
                        <Text
                            is='h1'
                            fontSize={fontSizes.lg}
                            fontWeight={fontWeights.bold}
                            textAlign='left'
                            lineHeight={lineHeights.tight}
                        >
                            {getText('pageTitle')}
                        </Text>
                    </Box>

                    {/* What Can Do Section */}
                    <Box css={{ marginBottom: `${space[5]}px` }}>
                        <Text
                            fontSize={fontSizes.md}
                            fontWeight={fontWeights.bold}
                            css={{ marginBottom: `${space[3]}px` }}
                            lineHeight={lineHeights.base}
                        >
                            {getText('whatCanDo')}
                        </Text>
                        {renderPermissions()}
                    </Box>

                    {/* Allow Access Button */}
                    <Box css={{ marginBottom: `${space[3]}px` }}>
                        <Button
                            type='button'
                            width='100%'
                            variant='primary'
                            onClick={handleApprove}
                            paddingY={space[3]}
                        >
                            {getText('allowAccessButton')}
                        </Button>
                    </Box>

                    {/* Cancel Button */}
                    <Box css={{ marginBottom: `${space[5]}px` }}>
                        <Button
                            type='button'
                            width='100%'
                            variant='secondary'
                            onClick={handleCancel}
                            paddingY={space[3]}
                        >
                            {getText('cancelButton')}
                        </Button>
                    </Box>

                    {/* Manage Access Text */}
                    <Box css={{ marginBottom: `${space[3]}px` }}>
                        <Text
                            fontSize={fontSizes.xs}
                            color={colors.gray}
                            lineHeight={lineHeights.base}
                            textAlign='left'
                        >
                            {getText('manageAccessText')}
                        </Text>
                    </Box>

                    {/* Privacy Policy Links */}
                    <Box>
                        <Text
                            fontSize={fontSizes.xs}
                            color={colors.gray}
                            lineHeight={lineHeights.base}
                            textAlign='left'
                        >
                            {getText('privacyPrefix')}{' '}
                            <Link
                                href='https://openai.com/policies/privacy-policy'
                                target='_blank'
                                color={colors.blue}
                                textDecoration='underline'
                            >
                                {getText('chatGPTPrivacyPolicy')}
                            </Link>
                            {getText('privacyMiddle')}{' '}
                            <Link
                                href='/privacy-policy'
                                target='_blank'
                                color={colors.blue}
                                textDecoration='underline'
                            >
                                {getText('sephoraPrivacyPolicy')}
                            </Link>
                            {getText('privacyEnd')}
                        </Text>
                    </Box>
                </Box>
                {/* End Content Container */}
            </Box>
        </div>
    );
};

const styles = {
    pageContainer: {
        paddingTop: space[7],
        paddingBottom: space[7],
        paddingLeft: space[3],
        paddingRight: space[3],
        backgroundColor: colors.white,
        position: 'relative'
    },
    logoContainer: {
        marginBottom: space[7]
    },
    logo: {
        height: space[5],
        width: 'auto'
    },
    errorContainer: {
        marginBottom: space[5]
    },
    titleContainer: {
        marginBottom: space[5]
    },
    permissionsSection: {
        marginBottom: space[5]
    },
    permissionsTitle: {
        marginBottom: space[3]
    },
    permissionItem: {
        marginBottom: space[3]
    },
    checkmark: {
        marginRight: space[2],
        lineHeight: lineHeights.base
    },
    allowButtonContainer: {
        marginBottom: space[3]
    },
    cancelButtonContainer: {
        marginBottom: space[5]
    },
    manageAccessContainer: {
        marginBottom: space[3]
    }
};

export default wrapFunctionalComponent(AuthGrant, 'AuthGrant');
